import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { 
  Clock, 
  Globe,
  AlertCircle,
  Calendar,
  CalendarCheck,
  ChevronLeft,
  ChevronRight,
  CheckCircle
} from 'lucide-react';
import { Button } from './ui/button';

const DAYS_OF_WEEK = [
  { id: 0, name: 'Monday', short: 'Mon' },
  { id: 1, name: 'Tuesday', short: 'Tue' },
  { id: 2, name: 'Wednesday', short: 'Wed' },
  { id: 3, name: 'Thursday', short: 'Thu' },
  { id: 4, name: 'Friday', short: 'Fri' },
  { id: 5, name: 'Saturday', short: 'Sat' },
  { id: 6, name: 'Sunday', short: 'Sun' }
];

// Format time for display (12-hour format)
const formatTimeDisplay = (time) => {
  if (!time) return '';
  const [hours, minutes] = time.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
  return `${displayHours}:${String(minutes).padStart(2, '0')} ${period}`;
};

// Generate 30-min time slots from a range
const generateTimeSlots = (startTime, endTime) => {
  const slots = [];
  const [startHour, startMin] = startTime.split(':').map(Number);
  const [endHour, endMin] = endTime.split(':').map(Number);
  
  let currentHour = startHour;
  let currentMin = startMin;
  
  while (currentHour < endHour || (currentHour === endHour && currentMin < endMin)) {
    const timeStr = `${String(currentHour).padStart(2, '0')}:${String(currentMin).padStart(2, '0')}`;
    slots.push(timeStr);
    
    currentMin += 30;
    if (currentMin >= 60) {
      currentMin = 0;
      currentHour++;
    }
  }
  
  return slots;
};

// Get user's timezone
const getUserTimezone = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

// Format date for display
const formatDate = (dateStr) => {
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString('en-US', { 
    weekday: 'short', 
    month: 'short', 
    day: 'numeric' 
  });
};

const TutorEffectiveAvailabilityView = ({ tutorId, title = "Available Slots" }) => {
  const { getAccessToken, user } = useAuth();
  const [effectiveData, setEffectiveData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [timezone] = useState(getUserTimezone());
  const [dateRange, setDateRange] = useState(() => {
    const today = new Date();
    const endDate = new Date(today);
    endDate.setDate(today.getDate() + 6);
    return {
      start: today.toISOString().split('T')[0],
      end: endDate.toISOString().split('T')[0]
    };
  });
  const fetchedRef = useRef(null);

  // Fetch effective availability
  useEffect(() => {
    const fetchKey = `${tutorId}-${dateRange.start}-${dateRange.end}`;
    if (fetchedRef.current === fetchKey && effectiveData) {
      return;
    }
    
    let isMounted = true;
    
    const fetchEffectiveAvailability = async () => {
      const targetTutorId = tutorId || user?.id;
      if (!targetTutorId) {
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        setError(null);
        const token = getAccessToken();
        
        if (!token) {
          setLoading(false);
          return;
        }

        const endpoint = `${process.env.REACT_APP_BACKEND_URL}/api/availability/${targetTutorId}/effective?start_date=${dateRange.start}&end_date=${dateRange.end}`;
        const response = await fetch(endpoint, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (!isMounted) return;

        if (response.ok) {
          const data = await response.json();
          if (!isMounted) return;
          fetchedRef.current = fetchKey;
          setEffectiveData(data);
          
          // Auto-select first date with available slots
          if (data.effective_slots && data.effective_slots.length > 0) {
            const firstDate = data.effective_slots[0].date;
            setSelectedDate(firstDate);
          } else {
            setSelectedDate(null);
          }
        } else if (response.status === 404) {
          if (!isMounted) return;
          fetchedRef.current = fetchKey;
          setEffectiveData({ effective_slots: [], weekly_availability: [] });
        } else {
          const err = await response.json();
          if (!isMounted) return;
          setError(err.detail || 'Failed to fetch availability');
        }
      } catch (err) {
        if (isMounted) {
          console.error('TutorEffectiveAvailabilityView: Fetch error:', err);
          setError('Failed to load availability');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchEffectiveAvailability();

    return () => {
      isMounted = false;
    };
  }, [tutorId, user?.id, dateRange, getAccessToken]);

  // Navigate to previous/next week
  const navigateWeek = (direction) => {
    const days = direction === 'prev' ? -7 : 7;
    const newStart = new Date(dateRange.start);
    newStart.setDate(newStart.getDate() + days);
    const newEnd = new Date(newStart);
    newEnd.setDate(newStart.getDate() + 6);
    
    // Don't allow navigating to past
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (newStart < today && direction === 'prev') {
      return;
    }
    
    fetchedRef.current = null; // Force refetch
    setDateRange({
      start: newStart.toISOString().split('T')[0],
      end: newEnd.toISOString().split('T')[0]
    });
  };

  // Get unique dates from effective slots
  const getUniqueDates = () => {
    if (!effectiveData?.effective_slots) return [];
    const dates = [...new Set(effectiveData.effective_slots.map(s => s.date))];
    return dates.sort();
  };

  // Get slots for a specific date
  const getSlotsForDate = (dateStr) => {
    if (!effectiveData?.effective_slots) return [];
    return effectiveData.effective_slots
      .filter(s => s.date === dateStr)
      .sort((a, b) => a.start_time.localeCompare(b.start_time));
  };

  // Get time slots from a slot range
  const getTimeSlotsFromSlot = (slot) => {
    return generateTimeSlots(slot.start_time, slot.end_time);
  };

  // Get all time slots for selected date
  const getTimeSlotsForDate = (dateStr) => {
    const dateSlots = getSlotsForDate(dateStr);
    let allSlots = [];
    dateSlots.forEach(slot => {
      const slots = getTimeSlotsFromSlot(slot);
      allSlots = [...allSlots, ...slots];
    });
    return allSlots;
  };

  // Get slot count for a date
  const getSlotCountForDate = (dateStr) => {
    const dateSlots = getSlotsForDate(dateStr);
    let totalSlots = 0;
    dateSlots.forEach(slot => {
      const slots = getTimeSlotsFromSlot(slot);
      totalSlots += slots.length;
    });
    return totalSlots;
  };

  const getSlotDotColor = (count) => {
    if (count === 0) return 'bg-gray-300';
    if (count <= 2) return 'bg-yellow-400';
    return 'bg-green-400';
  };

  // Generate all dates in range
  const getAllDatesInRange = () => {
    const dates = [];
    const current = new Date(dateRange.start);
    const end = new Date(dateRange.end);
    
    while (current <= end) {
      dates.push(current.toISOString().split('T')[0]);
      current.setDate(current.getDate() + 1);
    }
    return dates;
  };

  const isPastDate = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const startDate = new Date(dateRange.start);
    return startDate <= today;
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-600"></div>
          <span className="ml-3 text-gray-600 dark:text-gray-400">Loading availability...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg">
        <div className="flex items-center gap-2 text-red-600 dark:text-red-400">
          <AlertCircle className="w-5 h-5" />
          <span>{error}</span>
        </div>
      </div>
    );
  }

  const uniqueDates = getUniqueDates();
  const allDatesInRange = getAllDatesInRange();
  const selectedDateSlots = selectedDate ? getTimeSlotsForDate(selectedDate) : [];
  const hasAvailability = effectiveData?.weekly_availability?.length > 0;
  const hasEffectiveSlots = effectiveData?.effective_slots?.length > 0;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-500 to-emerald-600 p-6 text-white">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
            <CalendarCheck className="w-6 h-6" />
          </div>
          <div>
            <h2 className="text-xl font-bold">
              {effectiveData?.tutor_name ? `${effectiveData.tutor_name}'s ${title}` : title}
            </h2>
            <p className="text-green-100 text-sm">
              Showing available slots (excluding scheduled classes)
            </p>
          </div>
        </div>
      </div>

      <div className="p-6">
        {!hasAvailability ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            <Clock className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p className="text-lg font-medium">No availability set</p>
            <p className="text-sm mt-1">Weekly availability has not been configured yet.</p>
          </div>
        ) : (
          <>
            {/* Week Navigation */}
            <div className="flex items-center justify-between mb-6">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigateWeek('prev')}
                disabled={isPastDate()}
                className="flex items-center gap-1"
              >
                <ChevronLeft className="w-4 h-4" />
                Previous
              </Button>
              <div className="text-center">
                <p className="font-semibold text-gray-900 dark:text-white">
                  {formatDate(dateRange.start)} - {formatDate(dateRange.end)}
                </p>
                {effectiveData?.scheduled_count > 0 && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {effectiveData.scheduled_count} class(es) scheduled this week
                  </p>
                )}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigateWeek('next')}
                className="flex items-center gap-1"
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>

            {/* Date Selector Cards */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-700 dark:text-gray-300">Select a Date</h3>
              </div>
              
              <div className="grid grid-cols-7 gap-2">
                {allDatesInRange.map(dateStr => {
                  const slotCount = getSlotCountForDate(dateStr);
                  const isSelected = selectedDate === dateStr;
                  const hasSlots = slotCount > 0;
                  const date = new Date(dateStr + 'T00:00:00');
                  const dayName = DAYS_OF_WEEK[date.getDay() === 0 ? 6 : date.getDay() - 1].short;
                  const dayNum = date.getDate();
                  
                  return (
                    <button
                      key={dateStr}
                      onClick={() => hasSlots && setSelectedDate(dateStr)}
                      disabled={!hasSlots}
                      className={`
                        relative p-3 rounded-xl text-center transition-all duration-200
                        ${isSelected 
                          ? 'bg-green-50 dark:bg-green-900/30 border-2 border-green-500 shadow-md' 
                          : hasSlots 
                            ? 'bg-gray-50 dark:bg-gray-700 border-2 border-transparent hover:border-green-300 hover:shadow-sm cursor-pointer' 
                            : 'bg-gray-50 dark:bg-gray-700/50 border-2 border-transparent opacity-60 cursor-not-allowed'
                        }
                      `}
                    >
                      <p className={`text-xs font-medium mb-1 ${
                        isSelected ? 'text-green-600 dark:text-green-400' : 'text-gray-500 dark:text-gray-400'
                      }`}>
                        {dayName}
                      </p>
                      <p className={`text-lg font-bold mb-1 ${
                        isSelected ? 'text-green-700 dark:text-green-300' : 'text-gray-700 dark:text-gray-300'
                      }`}>
                        {dayNum}
                      </p>
                      <div className="flex flex-col items-center">
                        {hasSlots ? (
                          <>
                            <span className={`w-2 h-2 rounded-full mb-1 ${getSlotDotColor(slotCount)}`}></span>
                            <span className={`text-xs font-medium ${
                              isSelected ? 'text-green-700 dark:text-green-300' : 'text-gray-600 dark:text-gray-300'
                            }`}>
                              {slotCount} {slotCount === 1 ? 'slot' : 'slots'}
                            </span>
                          </>
                        ) : (
                          <span className="text-xs text-gray-400 dark:text-gray-500">Busy</span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Timezone & Duration Info */}
            <div className="flex items-center justify-between mb-4 text-sm text-gray-600 dark:text-gray-400">
              <div className="flex items-center gap-2">
                <Globe className="w-4 h-4" />
                <span>{timezone}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <span>30 min slots</span>
              </div>
            </div>

            {/* Time Slots Grid */}
            {selectedDate !== null && (
              <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                <h3 className="font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  Available Times for {formatDate(selectedDate)}
                </h3>
                
                {selectedDateSlots.length === 0 ? (
                  <p className="text-gray-500 dark:text-gray-400 text-center py-4">
                    No available slots for this date (all slots are booked)
                  </p>
                ) : (
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-2">
                    {selectedDateSlots.map((slot, index) => (
                      <div
                        key={index}
                        className="
                          px-3 py-2.5 rounded-lg text-center
                          bg-green-50 dark:bg-green-900/20 
                          text-green-700 dark:text-green-300
                          border border-green-200 dark:border-green-800
                          font-medium text-sm
                          hover:bg-green-100 dark:hover:bg-green-900/30
                          transition-colors cursor-default
                        "
                      >
                        {formatTimeDisplay(slot)}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* No effective slots message */}
            {!hasEffectiveSlots && hasAvailability && (
              <div className="mt-4 p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
                <div className="flex items-center gap-2 text-yellow-700 dark:text-yellow-300">
                  <AlertCircle className="w-5 h-5" />
                  <span className="font-medium">All slots are booked for this week</span>
                </div>
                <p className="text-sm text-yellow-600 dark:text-yellow-400 mt-1">
                  Try checking the next week for available slots.
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default TutorEffectiveAvailabilityView;
